import { map } from "rxjs/operators";
import { Injectable, inject } from "@angular/core";

import { Observable } from "rxjs";

import { environment } from "environments/environment";
import { FclRoute } from "./route-fcl.model";
import { LclRoute } from "./route-lcl.model";
import { HttpClient } from "@angular/common/http";
import { Route } from "./route-type";
import { RouteMapper } from "./route.mapper";
import { lclFormFields } from "../shared/form/fields/lcl-form-fields.const";
import { AirRoute } from "./route-air.model";
import { LtlRoute } from "./route-ltl.model";
import { AirTariffRoute } from "./route-airtariff.model";
import { FtlRoute } from "./route-ftl.model";
import { PtlRoute } from "./route-ptl.model";
import { RouteBatchItem, RouteBatchResponse } from "./route-batch.model";

@Injectable()
export class RouteRepository {
  private http = inject(HttpClient);

  public create(
    quoteId: number,
    originLocationId: number,
    destinationLocationId: number,
    flatRateRouteId: number,
    route?: Route,
  ): Observable<Route> {
    if (flatRateRouteId) {
      return this.createFlat(quoteId, flatRateRouteId, null, null, route);
    } else {
      return this.createLinked(
        quoteId,
        originLocationId,
        destinationLocationId,
        route,
      );
    }
  }

  public delete(route: Route): Observable<object> {
    return this.http.delete(
      `${environment.apiUrl}/${this.getEndpoint(route)}/${route.getId()}`,
    );
  }

  public update(route: Route): Observable<object> {
    const payload = this.getPayload({}, route);

    return this.http.patch(
      `${environment.apiUrl}/${this.getEndpoint(route)}/${route.getId()}`,
      payload,
    );
  }

  // The created routes come back in the order of `items`.
  public createBatch(
    quoteId: number,
    items: RouteBatchItem[],
  ): Observable<Route[]> {
    const isFlat = (item: RouteBatchItem) => !!item.flatRateRouteId;
    const payload = {
      routes: items
        .filter((item) => !isFlat(item))
        .map((item) =>
          this.getPayload(
            {
              rateOriginLocation: item.originLocationId,
              rateDestinationLocation: item.destinationLocationId,
            },
            item.route,
          ),
        ),
      flatRoutes: items
        .filter(isFlat)
        .map((item) =>
          this.getPayload({ flatRateRoute: item.flatRateRouteId }, item.route),
        ),
    };

    return this.http
      .post<RouteBatchResponse>(this.getBatchUrl(quoteId), payload)
      .pipe(
        map((response) => {
          const routes = response.routes.map((route) => RouteMapper.map(route));
          const flatRoutes = response.flatRoutes.map((route) =>
            RouteMapper.map(route),
          );

          return items.map((item) =>
            isFlat(item) ? flatRoutes.shift() : routes.shift(),
          );
        }),
      );
  }

  public updateBatch(quoteId: number, routes: Route[]): Observable<object> {
    const payload = (route: Route) =>
      this.getPayload({ id: route.getId() }, route);

    return this.http.patch(this.getBatchUrl(quoteId), {
      routes: routes.filter((route) => !this.isFlat(route)).map(payload),
      flatRoutes: routes.filter((route) => this.isFlat(route)).map(payload),
    });
  }

  public deleteBatch(quoteId: number, routes: Route[]): Observable<object> {
    return this.http.delete(this.getBatchUrl(quoteId), {
      body: {
        routes: routes
          .filter((route) => !this.isFlat(route))
          .map((route) => route.getId()),
        flatRoutes: routes
          .filter((route) => this.isFlat(route))
          .map((route) => route.getId()),
      },
    });
  }

  public createFlat(
    quoteId: number,
    flatRateRouteId: number,
    pickupRateRouteId?: number,
    deliveryRateRouteId?: number,
    route?: Route,
    quoteOriginLocation?: number,
    quoteDestinationLocation?: number,
    rateId?: number,
  ): Observable<Route> {
    const payload = this.getPayload({ flatRateRoute: flatRateRouteId }, route);
    if (pickupRateRouteId) {
      payload["pickupRateRoute"] = pickupRateRouteId;
    }
    if (deliveryRateRouteId) {
      payload["deliveryRateRoute"] = deliveryRateRouteId;
    }
    if (quoteOriginLocation) {
      payload["quoteOriginLocation"] = quoteOriginLocation;
    }
    if (quoteDestinationLocation) {
      payload["quoteDestinationLocation"] = quoteDestinationLocation;
    }
    if (rateId) {
      payload["rate"] = rateId;
    }

    return this.http
      .post(`${environment.apiUrl}/quote/${quoteId}/flatRoute`, payload)
      .pipe(map((response) => RouteMapper.map(response)));
  }

  private createLinked(
    quoteId: number,
    originLocationId: number,
    destinationLocationId: number,
    route?: Route,
  ): Observable<Route> {
    const payload = this.getPayload(
      {
        rateOriginLocation: originLocationId,
        rateDestinationLocation: destinationLocationId,
      },
      route,
    );

    return this.http
      .post(`${environment.apiUrl}/quote/${quoteId}/route`, payload)
      .pipe(map((response) => RouteMapper.map(response)));
  }

  private getBatchUrl(quoteId: number): string {
    return `${environment.apiUrl}/quote/${quoteId}/route/batch`;
  }

  private isFlat(route: Route): boolean {
    return this.getEndpoint(route) === "flatRoute";
  }

  private getEndpoint(route: Route): string {
    if (
      route.getDiscriminator().indexOf("flat") !== -1 ||
      route.getDiscriminator().indexOf("ltl") !== -1 ||
      route.getDiscriminator().indexOf("ftl") !== -1 ||
      route.getDiscriminator().indexOf("ptl") !== -1
    ) {
      return "flatRoute";
    } else {
      return "route";
    }
  }

  private getPayload(payload: object, route?: Route): object {
    if (route instanceof FclRoute) {
      payload["comment"] = route.getComment();
      payload["tier"] =
        route && route.getTier() ? route.getTier().getId() : null;
      payload["containers"] = route.getContainers().getRequestParams();
    }

    if (route instanceof LclRoute) {
      payload["comment"] = route.getComment();
      payload[lclFormFields.CHARGEABLE_WEIGHT_PRICE] = route
        ? route.getChargeableWeightPrice()
        : 0;
      payload[lclFormFields.BL_PRICE] = route ? route.getBlPrice() : 0;
    }

    if (route instanceof AirRoute) {
      payload["comment"] = route.getComment();
      payload[lclFormFields.CHARGEABLE_WEIGHT_PRICE] = route
        ? route.getChargeableWeightPrice()
        : 0;
      payload[lclFormFields.BL_PRICE] = route ? route.getBlPrice() : 0;
    }

    if (route instanceof AirTariffRoute) {
      payload["comment"] = route.getComment();
      payload["weightPriceLt45"] = route.weightPriceLt45;
      payload["weightPriceGt45"] = route.weightPriceGt45;
      payload["weightPriceGt100"] = route.weightPriceGt100;
      payload["weightPriceGt300"] = route.weightPriceGt300;
      payload["weightPriceGt500"] = route.weightPriceGt500;
      payload["weightPriceGt1000"] = route.weightPriceGt1000;
    }

    if (route instanceof LtlRoute) {
      payload[lclFormFields.CHARGEABLE_WEIGHT_PRICE] = route
        ? route.getChargeableWeightPrice()
        : 0;
    }

    if (route instanceof FtlRoute) {
      payload[lclFormFields.BL_PRICE] = route ? route.getBlPrice() : 0;
    }

    if (route instanceof PtlRoute) {
      payload[lclFormFields.COMMENT] = route.getComment();
      payload[lclFormFields.BL_PRICE] = route ? route.getBlPrice() : 0;
      payload[lclFormFields.CHARGEABLE_WEIGHT_PRICE] = route
        ? route.getChargeableWeightPrice()
        : 0;
    }

    return payload;
  }
}
